/**
 * 배너 상세 페이지 폼 스키마
 */

import { z } from 'zod';

// ============================================
// 상시 노출 날짜 유틸리티
// ============================================

/** 오늘 날짜 시작 시각 문자열 반환 (YYYY-MM-DDT00:00:00) */
export function getTodayStart(): string {
  return formatDateTime(new Date(), true);
}

/** 현재로부터 1년 뒤 종료일 반환 (YYYY-MM-DDT23:59:59) */
export function getOneYearLaterEnd(): string {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return formatDateTime(date, false);
}

/** endDate가 현재 시점 기준 6개월 이상 남았으면 상시 노출로 판별 */
export function isAlwaysVisibleDate(endDate: string | null | undefined): boolean {
  if (!endDate) return false;
  const end = new Date(endDate);
  const sixMonthsLater = new Date();
  sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
  return end >= sixMonthsLater;
}

function formatDateTime(date: Date, startOfDay: boolean): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}T${startOfDay ? '00:00:00' : '23:59:59'}`;
}

// ============================================
// 폼 스키마
// ============================================

/** 배너 폼 Zod 스키마 */
export const bannerFormSchema = z
  .object({
    name: z.string().min(1, '설명은 필수입니다'),
    bannerType: z.enum(['SURVEY', 'CURATION', 'AD', 'PARTNERSHIP', 'ETC'], {
      message: '배너 타입을 선택해주세요',
    }),
    isActive: z.boolean(),
    mediaType: z.enum(['IMAGE', 'VIDEO'], {
      message: '미디어 유형을 선택해주세요',
    }),
    imageUrl: z.string().min(1, '미디어 파일을 업로드해주세요'),
    posterUrl: z.string().min(1, '동영상 대표 이미지를 업로드해주세요').optional(),
    descriptionA: z.string(),
    descriptionB: z.string(),
    textPosition: z.enum(['LT', 'LB', 'RT', 'RB', 'CENTER'], {
      message: '텍스트 위치를 선택해주세요',
    }),
    nameFontColor: z
      .string()
      .regex(/^[0-9a-fA-F]{6}$/, '올바른 HEX 색상을 입력해주세요 (예: ffffff)'),
    descriptionFontColor: z
      .string()
      .regex(/^[0-9a-fA-F]{6}$/, '올바른 HEX 색상을 입력해주세요 (예: ffffff)'),
    targetUrl: z.string(),
    isExternalUrl: z.boolean(),
    isAlwaysVisible: z.boolean(),
    startDate: z.string({ error: '시작일을 입력해주세요' }).min(1, '시작일을 입력해주세요'),
    endDate: z.string({ error: '종료일을 입력해주세요' }).min(1, '종료일을 입력해주세요'),
    /** 큐레이션 ID (CURATION 타입인 경우에만 사용) */
    curationId: z.number().nullable(),
  })
  .refine(
    (data) => {
      // CURATION 타입인 경우 큐레이션 선택 필수
      if (data.bannerType === 'CURATION') {
        return data.curationId !== null;
      }
      return true;
    },
    {
      message: '큐레이션을 선택해주세요',
      path: ['curationId'],
    }
  )
  .superRefine((data, context) => {
    if (data.mediaType === 'VIDEO' && !data.posterUrl) {
      context.addIssue({
        code: 'custom',
        path: ['posterUrl'],
        message: '동영상 대표 이미지를 업로드해주세요',
      });
    }
  });

/** 배너 폼 타입 */
export type BannerFormValues = z.infer<typeof bannerFormSchema>;

/** 배너 폼 기본값 */
export const DEFAULT_BANNER_FORM: BannerFormValues = {
  name: '',
  bannerType: 'CURATION',
  isActive: true,
  mediaType: 'IMAGE',
  imageUrl: '',
  descriptionA: '',
  descriptionB: '',
  textPosition: 'LB',
  nameFontColor: 'ffffff',
  descriptionFontColor: 'ffffff',
  targetUrl: '',
  isExternalUrl: false,
  isAlwaysVisible: false,
  startDate: '',
  endDate: '',
  curationId: null,
};
